import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Alert, Button, Modal, Pagination, Skeleton, Table, Upload, theme } from "antd";
import { CheckCircleFilled, CloudUploadOutlined, DownloadOutlined, FileExcelOutlined, MailOutlined, MobileOutlined, ReloadOutlined } from "@ant-design/icons";
import { api, upload } from "./api";
import "./message-import.css";

export interface MessageImportValue { id: number; count: number }
interface PreviewRow { row_no: number; recipient_type: "phone" | "email"; recipient: string; body: string }
interface Preview {
  id: number; filename: string; total_rows: number; valid_rows: number; duplicate_rows: number;
  rows: PreviewRow[];
}
const templateURL = `data:text/csv;charset=utf-8,${encodeURIComponent("\uFEFFrecipient_type,recipient,body\nphone,+8613800138000,您好，您的预约已确认。\nemail,customer@example.com,您好，您的预约已确认。\n")}`;
function Recipient({ row }: { row: PreviewRow }) {
  return <div className="message-recipient"><span className="message-recipient-icon">{row.recipient_type === "email" ? <MailOutlined /> : <MobileOutlined />}</span><div><strong>{row.recipient}</strong><span>{row.recipient_type === "email" ? "邮箱" : "手机号码"}</span></div></div>;
}
export function MessageImport({ value, onChange, onBusyChange, published = false, readOnly = false }: {
  value?: MessageImportValue; onChange?: (value: MessageImportValue) => void;
  onBusyChange?: (busy: boolean) => void; published?: boolean; readOnly?: boolean;
}) {
  const { token } = theme.useToken();
  const [preview, setPreview] = useState<Preview>();
  const [busy, setBusy] = useState(false);
  const uploading = useRef(false);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);
  const [selected, setSelected] = useState<PreviewRow>();
  const [page, setPage] = useState(1);
  const style = {
    "--message-accent": token.colorPrimary, "--message-tint": token.colorPrimaryBg,
    "--message-line": token.colorBorderSecondary, "--message-text": token.colorText,
    "--message-muted": token.colorTextSecondary, "--message-fill": token.colorFillQuaternary,
    "--message-bg": token.colorBgContainer, "--message-success": token.colorSuccess,
  } as CSSProperties;
  useEffect(() => {
    let active = true;
    setPreview(undefined);
    setPage(1);
    if (value?.id) {
      api<Preview>(`/message-imports/${value.id}?business=sms`).then(result => {
        if (active) { setPreview(result); setError(""); }
      }).catch(e => { if (active) setError((e as Error).message); });
    }
    return () => { active = false; };
  }, [value?.id, reload]);
  const choose = async (file: File) => {
    if (uploading.current) return;
    if (!/\.(csv|xlsx)$/i.test(file.name) || file.size > 16 * 1024 * 1024) {
      setError("请选择不超过 16 MB 的 CSV 或 XLSX 文件"); return;
    }
    uploading.current = true;
    setBusy(true); onBusyChange?.(true); setError("");
    try {
      const data = new FormData(); data.append("file", file);
      const result = await upload<Preview>("/message-imports?business=sms", data);
      onChange?.({ id: result.id, count: result.valid_rows });
      setPreview(result); setPage(1);
    } catch (e) { setError((e as Error).message); }
    finally { uploading.current = false; setBusy(false); onBusyChange?.(false); }
  };
  const uploadProps = { accept: ".csv,.xlsx", multiple: false, showUploadList: false, disabled: busy, beforeUpload: (file: File) => { void choose(file); return false; } };
  return <div className="sms-import" style={style} aria-busy={busy}>
    {!readOnly && <>
      {!value?.id && <Upload.Dragger {...uploadProps} className="message-upload">
        <span className="message-upload-icon"><CloudUploadOutlined spin={busy} /></span>
        <strong>{busy ? "正在导入，请稍候…" : "点击上传或将文件拖到这里"}</strong>
        <p>导入手机号码或邮箱，以及每位收件人对应的 iMessage 内容</p>
        <span className="message-upload-formats">CSV / Excel<span>最多 10,000 条</span><span>单个文件不超过 16 MB</span></span>
      </Upload.Dragger>}
      <div className="message-import-tools">
        <span>{value?.id ? "需要调整收件人或内容？" : "第一次导入？按模板填写即可"}</span>
        <div>
          {value?.id && <Upload {...uploadProps}><Button type="link" icon={<ReloadOutlined />} loading={busy}>重新导入</Button></Upload>}
          <Button type="link" icon={<DownloadOutlined />} href={templateURL} download="iMessage导入模板.csv">下载模板</Button>
          <Button type="link" onClick={() => setHelpOpen(true)}>填写示例</Button>
        </div>
      </div>
      {published && <Alert showIcon type="info" message="重新导入的内容将在审核通过后加入任务，已领取的信息不受影响。" />}
    </>}
    {error && <Alert showIcon type="error" message={preview ? "新文件未导入，原有内容已保留" : value?.id ? "暂时无法读取导入内容" : "导入未完成"} description={error} action={value?.id ? <Button size="small" onClick={() => setReload(x => x + 1)}>重新加载</Button> : undefined} />}
    {value?.id && !preview && !error && <div className="message-import-loading" role="status"><p>正在读取 {value.count.toLocaleString()} 条信息…</p><Skeleton active title={false} paragraph={{ rows: 3 }} /></div>}
    {preview && <>
      <div className="message-file-summary" role="status">
        <div className="message-file-heading"><span className="message-file-icon"><FileExcelOutlined /></span><div><strong title={preview.filename}>{preview.filename}</strong><span><CheckCircleFilled /> {readOnly ? "已随任务保存" : "导入完成"}</span></div></div>
        <div className="message-import-counts"><div><strong>{preview.valid_rows.toLocaleString()}</strong><span>有效信息</span></div><div><strong>{preview.duplicate_rows.toLocaleString()}</strong><span>重复已去除</span></div><div><strong>{preview.total_rows.toLocaleString()}</strong><span>文件总条数</span></div></div>
      </div>
      <div className="message-preview-heading"><h4>收件人与内容预览</h4><span>{preview.valid_rows > preview.rows.length ? `展示前 ${preview.rows.length} 条，共 ${preview.valid_rows.toLocaleString()} 条` : `共 ${preview.valid_rows.toLocaleString()} 条有效信息`}</span></div>
      <div className="message-preview-table"><Table size="small" rowKey="row_no" dataSource={preview.rows.slice((page - 1) * 5, page * 5)} pagination={false} tableLayout="fixed" columns={[
        { title: "行号", dataIndex: "row_no", width: 56, render: n => <span className="message-row-number">{n}</span> },
        { title: "收件人", key: "recipient", width: "38%", render: (_, row) => <Recipient row={row} /> },
        { title: "iMessage 内容", dataIndex: "body", render: text => <p className="message-body-excerpt">{text}</p> },
        { title: "", key: "view", width: 60, render: (_, row) => <Button type="link" size="small" aria-label={`查看第 ${row.row_no} 行信息`} onClick={() => setSelected(row)}>查看</Button> },
      ]} /></div>
      <div className="message-preview-cards">{preview.rows.slice((page - 1) * 5, page * 5).map(row => <article className="message-preview-card" key={row.row_no}>
        <Recipient row={row} /><div className="message-card-copy"><p className="message-body-excerpt">{row.body}</p></div><div className="message-preview-card-footer"><span>文件第 {row.row_no} 行</span><Button type="link" size="small" aria-label={`查看第 ${row.row_no} 行信息`} onClick={() => setSelected(row)}>查看完整内容</Button></div>
      </article>)}</div>
      {preview.rows.length > 5 && <Pagination className="message-preview-pagination" size="small" current={page} pageSize={5} total={preview.rows.length} showSizeChanger={false} onChange={setPage} />}
      {!readOnly && <p className="message-import-note">保存草稿后可继续编辑，审核通过后开始投放。停发收件人会在发布时自动过滤。</p>}
    </>}
    <Modal title="如何填写导入文件" open={helpOpen} onCancel={() => setHelpOpen(false)} width={640} footer={<Button type="primary" icon={<DownloadOutlined />} href={templateURL} download="iMessage导入模板.csv">下载模板</Button>}>
      <div className="message-import-help" style={style}>
        <p>下载模板后，每行填写一位收件人及对应内容。手机号码和邮箱可以放在同一份文件中，请保留模板的第一行列名。</p>
        <dl><div><dt>收件人类型 <code>recipient_type</code></dt><dd>手机号码填写 <code>phone</code>，邮箱填写 <code>email</code>。</dd></div><div><dt>收件人地址 <code>recipient</code></dt><dd>填写完整手机号码或邮箱。号码列请设为文本，避免 Excel 改变号码格式。</dd></div><div><dt>发送内容 <code>body</code></dt><dd>填写完整的 iMessage 内容，每位收件人的内容可以不同。</dd></div></dl>
        <div className="message-template-example"><strong>填写示例</strong><div><span>phone</span><span>+8613800138000</span><p>您好，您的预约已确认。</p></div><div><span>email</span><span>customer@example.com</span><p>您好，您的服务已更新。</p></div></div>
        <p className="message-import-note">支持 CSV 和单工作表 XLSX，最多 10,000 行、16 MB。导入仅校验格式，收件人需已开通 iMessage；实际发送结果以 iPhone 执行为准。</p>
      </div>
    </Modal>
    <Modal title="iMessage 内容预览" open={!!selected} onCancel={() => setSelected(undefined)} footer={<Button onClick={() => setSelected(undefined)}>关闭预览</Button>}>
      {selected && <div className="message-content-detail" style={style}><Recipient row={selected} /><div className="message-content-label">iMessage · 文件第 {selected.row_no} 行</div><p>{selected.body}</p></div>}
    </Modal>
  </div>;
}
